import {
  useFieldArray,
  UseFieldArrayProps,
  UseFormReturn,
} from "react-hook-form";
import {
  Input,
  FieldError,
  FlexRow,
  Label,
  Legend,
  RepeaterFields,
  RepeaterField,
  Select,
  TextArea,
} from "atoms";
import { ImageUpload, UploadedImage } from "features/images";
import { ArtistFormFields } from "../types";
import { LinksInput } from "./LinksInput";

type ArtistCollectionInputProps = {
  control: UseFieldArrayProps<ArtistFormFields>["control"];
  errors?: UseFormReturn<ArtistFormFields>["formState"]["errors"];
  register: UseFormReturn<ArtistFormFields>["register"];
  createCollectionUploadSetter: (
    index: number
  ) => (upload: UploadedImage) => void;
};

function ArtistCollectionsInput({
  control,
  errors,
  register,
  createCollectionUploadSetter,
  ...props
}: ArtistCollectionInputProps) {
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: "artistCollections",
  });

  const handleAppend = () =>
    append({ collectionName: "", collectionLinks: [] });

  return (
    <Label as="fieldset" {...props}>
      <Legend>Artist Collections</Legend>

      <RepeaterFields handleAppend={handleAppend} appendLabel="Add Collection">
        {fields.map((item, index) => (
          <RepeaterField
            key={item.id}
            {...RepeaterField.createHandlers(
              index,
              fields.length,
              move,
              remove
            )}
          >
            <Label>
              Collection Name
              <Input
                {...register(`artistCollections.${index}.collectionName`, {
                  required: "This field is required",
                })}
                isInvalid={!!errors?.artistCollections?.[index]?.collectionName}
              />
              {errors?.artistCollections?.[index]?.collectionName && (
                <FieldError>
                  {errors?.artistCollections?.[index]?.collectionName?.message}
                </FieldError>
              )}
            </Label>

            <fieldset>
              <Legend>Thumbnail</Legend>

              <ImageUpload
                fieldRegister={register(
                  `artistCollections.${index}.collectionImage`,
                  {
                    required: "Collections require a thumbnail image.",
                  }
                )}
                onSuccess={createCollectionUploadSetter(index)}
                entityType="PROJECT"
                src={item.collectionImage?.url}
                small
              />
              {errors?.artistCollections?.[index]?.collectionImage && (
                <FieldError>
                  {errors?.artistCollections?.[index]?.collectionImage?.message}
                </FieldError>
              )}
            </fieldset>

            <Label>
              Description
              <TextArea
                {...register(
                  `artistCollections.${index}.collectionDescription`
                )}
              />
            </Label>

            <FlexRow itemsFlex={1}>
              <Label>
                Floor Price
                <Input
                  type="number"
                  step="any"
                  min="0"
                  {...register(
                    `artistCollections.${index}.collectionFloorPrice`,
                    { valueAsNumber: true }
                  )}
                  isInvalid={
                    !!errors?.artistCollections?.[index]?.collectionFloorPrice
                  }
                />
                {errors?.artistCollections?.[index]?.collectionFloorPrice && (
                  <FieldError>
                    {
                      errors?.artistCollections?.[index]?.collectionFloorPrice
                        ?.message
                    }
                  </FieldError>
                )}
              </Label>

              <Label>
                Collection Size
                <Input
                  type="number"
                  step="any"
                  min="0"
                  {...register(`artistCollections.${index}.collectionSize`, {
                    valueAsNumber: true,
                  })}
                  isInvalid={
                    !!errors?.artistCollections?.[index]?.collectionSize
                  }
                />
                {errors?.artistCollections?.[index]?.collectionSize && (
                  <FieldError>
                    {
                      errors?.artistCollections?.[index]?.collectionSize
                        ?.message
                    }
                  </FieldError>
                )}
              </Label>
            </FlexRow>

            <FlexRow itemsFlex={1}>
              <Label>
                Status
                <Select
                  {...register(`artistCollections.${index}.collectionStatus`)}
                >
                  <option value="PUBLISHED">Published</option>
                  <option value="DRAFT">Draft</option>
                </Select>
              </Label>

              <Label>
                Type
                <Select
                  {...register(`artistCollections.${index}.collectionType`)}
                >
                  <option value="1/1">1/1</option>
                  <option value="EDITIONS">Editions</option>
                </Select>
              </Label>
            </FlexRow>

            <LinksInput<ArtistFormFields>
              control={control}
              register={register}
              name={`artistCollections.${index}.collectionLinks`}
              marketplacesOnly
            />
          </RepeaterField>
        ))}
      </RepeaterFields>
    </Label>
  );
}

export { ArtistCollectionsInput };
